import React, { useCallback, useEffect, useState } from 'react';

import { ModalHeader } from '../../components/ModalHeader';
import { Icon } from '../../components/Icon';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { TrainingsService } from '../../services/TrainingsService';
import { TrainingTemplatesService } from '../../services/TrainingTemplatesService';
import { formatSplitLabel } from '../../utils/formatSplitLabel';
import { IExercise } from '../../domains/Exercise';
import { ITrainingTemplate } from '../../domains/TrainingTemplate';
import { AddExercisesInTraining } from '../addExercisesInTraining';

import {
  Actions,
  ActionsRight,
  AddExerciseButton,
  Body,
  Container,
  DestructiveWarning,
  EmptyPreview,
  EmptyState,
  ExerciseRow,
  GhostButton,
  PreviewDay,
  PreviewDayHeader,
  PrimaryButton,
  RemoveButton,
  StepTitle,
  TemplateCard,
  TemplateList,
} from './styles';

interface ISelectTrainingTemplateProps {
  user_avatar?: string;
  user_id: string;
  onApplied: () => void;
}

type Step = 'list' | 'preview';

interface IDraftDay {
  split_index: number;
  exercises: IExercise[];
}

function mainMuscleGroupName(exercise: IExercise): string {
  const main =
    exercise.muscle_groups?.find(group => group.is_main) ||
    exercise.muscle_groups?.[0];

  return main?.name || main?.muscle_group?.name || 'Outro';
}

const SelectTrainingTemplate: React.FC<ISelectTrainingTemplateProps> = ({
  user_avatar,
  user_id,
  onApplied,
}) => {
  const { addModal, removeModal } = useModal();
  const { addToast } = useToast();
  const trainingsService = new TrainingsService();
  const trainingTemplatesService = new TrainingTemplatesService();

  const [step, setStep] = useState<Step>('list');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState<ITrainingTemplate[]>([]);

  const [selectedTemplate, setSelectedTemplate] =
    useState<ITrainingTemplate | null>(null);
  const [days, setDays] = useState<IDraftDay[]>([]);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    trainingTemplatesService
      .getAll()
      .then(response => setTemplates(response.data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos templates de treino.',
        });
      })
      .finally(() => setLoadingTemplates(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectTemplate = useCallback((template: ITrainingTemplate) => {
    setSelectedTemplate(template);
    setDays(
      template.splits.map((split, index) => ({
        split_index: index,
        exercises: split.exercises.map(
          splitExercise => splitExercise.exercise,
        ),
      })),
    );
    setStep('preview');
  }, []);

  const handleRemoveExercise = useCallback(
    (split_index: number, exercise_id: string) => {
      setDays(current =>
        current.map(day =>
          day.split_index === split_index
            ? {
                ...day,
                exercises: day.exercises.filter(
                  exercise => exercise.id !== exercise_id,
                ),
              }
            : day,
        ),
      );
    },
    [],
  );

  const handleAddExercise = useCallback(
    (day: IDraftDay) => {
      addModal(
        <AddExercisesInTraining
          training_number={day.split_index}
          exercises_already_registered_of_day={day.exercises}
          fromSuggestion
          onAdd={async ({ exercise }) => {
            setDays(current =>
              current.map(item =>
                item.split_index === day.split_index
                  ? { ...item, exercises: [...item.exercises, exercise] }
                  : item,
              ),
            );
          }}
        />,
      );
    },
    [addModal],
  );

  const totalExercises = days.reduce(
    (sum, day) => sum + day.exercises.length,
    0,
  );

  const handleApply = useCallback(async () => {
    if (!selectedTemplate) return;

    setApplying(true);

    try {
      await trainingsService.applyTrainingTemplate({
        user_id,
        template_id: selectedTemplate.id,
        days: days.map(day => ({
          split_index: day.split_index,
          exercise_ids: day.exercises.map(exercise => exercise.id),
        })),
      });

      addToast({ type: 'success', title: 'Template aplicado.' });
      onApplied();
      removeModal();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao aplicar template',
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setApplying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, days, user_id, onApplied, removeModal]);

  return (
    <Container>
      <ModalHeader title="Selecionar template de treino" avatarUrl={user_avatar} />

      {step === 'list' && (
        <Body>
          <StepTitle>Escolha um template pra aplicar neste aluno</StepTitle>

          {loadingTemplates ? (
            <EmptyState>Carregando...</EmptyState>
          ) : templates.length === 0 ? (
            <EmptyState>Nenhum template de treino cadastrado ainda.</EmptyState>
          ) : (
            <TemplateList>
              {templates.map(template => {
                const exerciseCount = template.splits.reduce(
                  (sum, split) => sum + split.exercises.length,
                  0,
                );

                return (
                  <TemplateCard
                    key={template.id}
                    type="button"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <strong>{template.name}</strong>
                    <span>{template.focus}</span>
                    <span className="meta">
                      {template.splits.length} splits · {exerciseCount}{' '}
                      exercícios
                    </span>
                  </TemplateCard>
                );
              })}
            </TemplateList>
          )}
        </Body>
      )}

      {step === 'preview' && (
        <>
          <Body>
            <DestructiveWarning>
              Isso vai substituir os exercícios já cadastrados em{' '}
              {days
                .map(day => formatSplitLabel(day.split_index).title)
                .join(', ')}
              .
            </DestructiveWarning>

            {days.map(day => (
              <PreviewDay key={day.split_index}>
                <PreviewDayHeader>
                  <strong>{formatSplitLabel(day.split_index).title}</strong>
                </PreviewDayHeader>

                {day.exercises.length === 0 ? (
                  <EmptyPreview>Nenhum exercício neste split.</EmptyPreview>
                ) : (
                  day.exercises.map(exercise => (
                    <ExerciseRow key={exercise.id}>
                      <span>
                        {exercise.name}{' '}
                        <span className="muscle">
                          {mainMuscleGroupName(exercise)}
                        </span>
                      </span>
                      <RemoveButton
                        type="button"
                        onClick={() =>
                          handleRemoveExercise(day.split_index, exercise.id)
                        }
                      >
                        <Icon name="close" size={16} color="currentColor" />
                      </RemoveButton>
                    </ExerciseRow>
                  ))
                )}

                <AddExerciseButton
                  type="button"
                  onClick={() => handleAddExercise(day)}
                >
                  <Icon name="add" size={15} color="currentColor" />
                  Adicionar exercício
                </AddExerciseButton>
              </PreviewDay>
            ))}
          </Body>
          <Actions>
            <GhostButton type="button" onClick={() => setStep('list')}>
              Voltar
            </GhostButton>
            <ActionsRight>
              <PrimaryButton
                type="button"
                disabled={applying || totalExercises === 0}
                onClick={handleApply}
              >
                {applying ? 'Aplicando...' : 'Aplicar template'}
              </PrimaryButton>
            </ActionsRight>
          </Actions>
        </>
      )}
    </Container>
  );
};

export { SelectTrainingTemplate };
